import { Flex } from "@mantine/core"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { BackOrderApi } from "../../apis"
import { IGeneral } from "../../store/General"
import { usePermission } from "../permission"
import { ControlPanel } from "../odoo/control-panel"
import { ColumnToggle } from "../odoo/column-toggle"
import { GroupByOption } from "../odoo/group-by-menu"
import { ITableRef, Table } from "../table"
import { useBackOrderFilters } from "./back-order-filters"
import { useBackOrderColumns, getBackOrderColumnKeys, filterVisibleBackOrderColumns } from "./back-order-columns"
import { BackOrderFilterPanel } from "./back-order-filter-panel"

export function BackOrderList() {
  const { t } = useTranslation(["order", "common", "supplierSales"])

  const GROUP_BY_OPTIONS: GroupByOption[] = [
    { key: "orderStatus", label: t("order:backOrder.poStatus") },
    { key: "purchaseType", label: t("order:backOrder.poType") },
    { key: "senderBusinessPartnerRefCodeBusinessName", label: t("order:backOrder.buyer") },
    { key: "paymentStatus", label: t("order:backOrder.paymentStatus") },
  ]

  const { orderStatus, paymentStatus, deliveryNoteStatus, invoiceStatus, orderPurchaseTypes } = useSelector(
    (state: { general: IGeneral }) => state.general,
  )

  const ORD_BCK_ORD = usePermission("ORD_BCK_ORD")
  const tableRef = useRef<ITableRef>(null)
  const [filterOpen, setFilterOpen] = useState(false)
  const [groupBy, setGroupBy] = useState<string | null>(null)
  const columnKeys = useMemo(() => getBackOrderColumnKeys(t), [t])
  const [columnVisibility, setColumnVisibility] = useState(columnKeys)
  useEffect(() => {
    setColumnVisibility((prev) => columnKeys.map((k) => ({ ...k, visible: prev.find((p) => p.key === k.key)?.visible ?? k.visible })))
  }, [columnKeys])

  const { filters, setFilter, clearFilters, activeFilterChips, searchQuery } = useBackOrderFilters()

  const reload = () => tableRef.current?.reload()

  const columns = useBackOrderColumns({
    orderStatus,
    paymentStatus,
    deliveryNoteStatus,
    invoiceStatus,
    orderPurchaseTypes,
    onClick: (_key, _record) => {},
    ORD_BCK_ORD,
  })

  const visibleColumns = filterVisibleBackOrderColumns(columns, columnVisibility)

  return (
    <Flex direction="column" gap="sm" style={{ minHeight: "60vh" }}>
      <ControlPanel
        searchValue={searchQuery}
        onSearchChange={(v) => setFilter("searchQuery", v)}
        onSearch={() => reload()}
        filterChips={activeFilterChips}
        onFilterClick={() => setFilterOpen(!filterOpen)}
        filterOpen={filterOpen}
        groupByOptions={GROUP_BY_OPTIONS}
        activeGroupBy={groupBy}
        onGroupByChange={setGroupBy}
        extraActions={<ColumnToggle columns={columnVisibility} onChange={setColumnVisibility} storageKey="back-order-columns" />}
      />

      <BackOrderFilterPanel
        opened={filterOpen}
        filters={filters}
        setFilter={setFilter}
        clearFilters={clearFilters}
        orderStatus={orderStatus}
        paymentStatus={paymentStatus}
        deliveryNoteStatus={deliveryNoteStatus}
        invoiceStatus={invoiceStatus}
        orderPurchaseTypes={orderPurchaseTypes}
        onSearch={reload}
      />

      {ORD_BCK_ORD.hasAccess ? (
        <Table ref={tableRef} name="back-order.list" filters={filters} columns={visibleColumns} loadData={(data) => BackOrderApi.list(data!)} />
      ) : (
        ORD_BCK_ORD.accessDenied()
      )}
    </Flex>
  )
}
